import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import PageContainer from "../components/layout/PageContainer";
import SectionTitle from "../components/common/SectionTitle";
import Input from "../components/common/Input";
import Button from "../components/common/Button";
import { isValidEmail, isEmpty } from "../utils/validators";
import * as authService from "../services/authService";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [form, setForm] = useState({
    email: "",
    password: "",
  });

  const [errors, setErrors] = useState({});
  const [serverMessage, setServerMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const from = location.state?.from?.pathname || "/profile";

  const validateForm = () => {
    const nextErrors = {};

    if (!isValidEmail(form.email)) {
      nextErrors.email = "Please enter a valid email address.";
    }

    if (isEmpty(form.password)) {
      nextErrors.password = "Please enter your password.";
    }

    return nextErrors;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));

    setServerMessage("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const nextErrors = validateForm();
    setErrors(nextErrors);
    setServerMessage("");

    if (Object.keys(nextErrors).length > 0) return;

    try {
      setLoading(true);

      const data = await authService.login({
        email: form.email.trim(),
        password: form.password,
      });

      if (data?.statusCode && data.statusCode !== 200) {
        throw new Error(data.message || "Sign in failed.");
      }

      authService.persistLogin(data);

      if (data?.role === "ADMIN") {
        navigate("/admin", { replace: true });
      } else {
        navigate(from, { replace: true });
      }
    } catch (err) {
      setServerMessage(err?.message || "We could not sign you in.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-section">
      <PageContainer width="md">
        <div className="mx-auto max-w-xl content-card-lg p-8">
          <SectionTitle
            eyebrow="Welcome Back"
            title="Sign in to your account"
            subtitle="Access your profile, review your bookings, and enjoy a faster checkout experience the next time you book."
            className="mb-8"
          />

          {serverMessage ? (
            <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 p-4">
              <p className="text-sm font-medium text-red-700">{serverMessage}</p>
            </div>
          ) : null}

          <form onSubmit={handleSubmit} className="space-y-5">
            <Input
              label="Email"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              error={errors.email}
              required
              placeholder="Enter your email address"
            />

            <Input
              label="Password"
              name="password"
              type="password"
              value={form.password}
              onChange={handleChange}
              error={errors.password}
              required
              placeholder="Enter your password"
              showPasswordToggle
            />

            <div className="flex items-center justify-between gap-4">
              <Link
                to="/forgot-password"
                className="text-sm font-medium text-henna hover:underline"
              >
                Forgot your password?
              </Link>
            </div>

            <Button type="submit" variant="primary" size="lg" fullWidth loading={loading}>
              Sign In
            </Button>
          </form>

          <p className="mt-6 text-sm text-muted-warm">
            Don&apos;t have an account?{" "}
            <Link to="/register" className="font-semibold text-henna hover:underline">
              Create one here
            </Link>
          </p>
        </div>
      </PageContainer>
    </div>
  );
};

export default Login;